import type { ChangeEvent } from "react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import type { SortingRule } from "react-table";
import useSWR from "swr";
import { useQueryParams, StringParam, NumberParam } from "use-query-params";
import { useAuthContext } from "../../../../../components/Auth";
import { PrimaryButtonWithPlusIcon } from "../../../../../components/Buttons/Buttons";
import { createChangePerPageHandler } from "../../../../../components/Pagination/util";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { TablePageLayout } from "../../../../../components/TablePage/TablePageLayout";
import { PageWrapper } from "../../../../../layout/portalPageLayout";
import type { UUID } from "../../../../../types/types";
import type { TenantAttributeGroupSummary } from "../../../../../types/types.PIM";
import { useDebounce } from "../../../../../util/hooks";
import { useRoutePath } from "../../../../../util/Routing";
import {
  useStoreState,
  rowHover,
  defaultHandleSort,
  DEFAULT_PER_PAGE,
} from "../../../../../util/util";
import { AttributesNav } from "../components/AttributesNav";
import { NameWithSystemDefaultIcon } from "../components/PIM.components.util";
import { CreateNewAttributeGroup } from "./CreateNewAttributeGroup";
import type { ColumnDef, Getter } from "@tanstack/react-table";

export function SellerAdminPIMAttributeGroups() {
  const [query, setQuery] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
  });
  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [offset, setOffset] = useState(query?.offset ?? 0);
  const [perPage, setPerPage] = useState(query?.perPage ?? DEFAULT_PER_PAGE);
  const perPageItems = [10, 20, 50];
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });
  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "asc",
  });
  const history = useHistory();
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { adminPath } = useRoutePath();
  const [showCreateGroupForm, setShowCreateGroupForm] = useState(false);

  const constructQuery = ({
    baseUrl,
    query,
  }: {
    baseUrl: string;
    query: string;
  }) => {
    const paramsWithOffset = new URLSearchParams(
      `offset=${offset}&limit=${perPage}`
    );
    if (query) paramsWithOffset.append("q", query);
    paramsWithOffset.append("order_by", sortingRules.orderBy || "asc");
    if (sortingRules.sortBy) {
      paramsWithOffset.append("sort_by", sortingRules.sortBy);
    }

    return baseUrl + paramsWithOffset;
  };

  const {
    data: attributeGroupsResponse,
    error: attributesGroupsError,
    mutate: mutateAttributeGroups,
  } = useSWR<TenantAttributeGroupSummary>(
    constructQuery({
      baseUrl: `/v2/tenants/${tenant_id}/pim/groups?`,
      query: debouncedSearchQuery,
    })
  );

  const isLoading = !attributeGroupsResponse && !attributesGroupsError;
  const [tableData, setTableData] = useState<
    {
      attribute_count: number;
      name: { name: string; is_system: boolean; id: string };
      id: UUID;
      display_name: string;
    }[]
  >([]);
  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);
  const { hasPermission } = useAuthContext();

  const tableColumns = React.useMemo<
    ColumnDef<{
      attribute_count: number;
      name: { name: string; is_system: boolean; id: string };
      id: UUID;
      display_name: string;
    }>[]
  >(
    () => [
      {
        header: t("Group Name"),
        accessorKey: "name",
        cell: ({
          getValue,
        }: {
          getValue: Getter<{ name: string; is_system: boolean; id: string }>;
        }) => (
          <NameWithSystemDefaultIcon
            name={getValue().name}
            id={getValue().id}
            is_system={getValue().is_system}
          />
        ),
      },
      {
        header: t("Display Name"),
        accessorKey: "display_name",
      },
      {
        // I feel like this wrapping in styled divs shoudln't be needed but this
        // is a way to make it look like the design.
        header: t("No. of Attributes"),
        accessorKey: "attribute_count",
        cell: ({ getValue }: { getValue: Getter<number> }) => (
          <div style={{ textAlign: "left" }}>{getValue()}</div>
        ),
      },
    ],
    [t]
  );

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  useEffect(() => {
    const handleGroupData = ({
      data,
      pagination,
    }: TenantAttributeGroupSummary) => {
      setTableData(
        data.map((group) => ({
          id: group.id,
          attribute_count: group.attribute_count,
          display_name: group.display_name ? t([group.display_name]) : "",
          name: { name: group.name, id: group.id, is_system: group.is_system },
        }))
      );
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    };

    if (attributeGroupsResponse) {
      handleGroupData(attributeGroupsResponse);
    }
  }, [attributeGroupsResponse, setTableData, perPage, t]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setQuery({ q: undefined });
    setOffset(0);
  };

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  useEffect(() => {
    if (debouncedSearchQuery === "") setQuery({ q: undefined });
    if (debouncedSearchQuery) {
      setQuery({ q: debouncedSearchQuery });
    }
  }, [setQuery, query, debouncedSearchQuery]);

  const handleRowClick = (e: React.MouseEvent) => {
    const params = new URLSearchParams();
    if (query.q) {
      params.append("q", query.q);
    }
    params.append("offset", String(offset));
    params.append("perPage", String(perPage));
    history.push(
      `${adminPath}/pim/attributes/groups/${e?.currentTarget?.id}?${params}`
    );
  };

  return (
    <PageWrapper>
      <AttributesNav tabName="Groups" />
      <SlideOut
        title={t("Create Group")}
        show={showCreateGroupForm}
        closeFlyout={() => setShowCreateGroupForm(false)}
      >
        <CreateNewAttributeGroup
          onSuccess={() => {
            setShowCreateGroupForm(false);
            mutateAttributeGroups();
          }}
          onCancel={() => setShowCreateGroupForm(false)}
        />
      </SlideOut>
      <TablePageLayout
        showTitle={false}
        searchQuery={searchQuery}
        searchPlaceholder={t("Search by Group name or Attribute name")}
        onSearchChange={handleSearch}
        onSearchClear={handleClearSearch}
        toolbarActions={
          hasPermission("modify_groups") ? (
            <PrimaryButtonWithPlusIcon
              onClick={() => setShowCreateGroupForm(true)}
              data-alignright
              useV2
            >
              <span style={{ fontSize: "15px" }}>{t("Create")}</span>
            </PrimaryButtonWithPlusIcon>
          ) : undefined
        }
        perPageItems={perPageItems}
        perPage={perPage}
        onPerPageChange={changePerPage}
        columns={tableColumns}
        data={tableData}
        isLoading={isLoading}
        error={attributesGroupsError}
        rowClick={handleRowClick}
        rowHover={rowHover}
        handleSort={handleSort}
        lastChildleftAlign
        pagination={tablePagination}
        totalCount={attributeGroupsResponse?.pagination?.total}
        offset={offset}
        onPageChange={changePage}
      />
    </PageWrapper>
  );
}
